import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);

import { CommonModule, CurrencyPipe, DecimalPipe } from '@angular/common';
import { Component, Input, ViewChild, ElementRef } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration, ChartData } from 'chart.js';
import { PaymentReport } from '../../../../domain/models/payment-report.model';
import { toCsv, downloadCsv, exportReportPdf } from '../report-export.util';

@Component({
  selector: 'app-summary-tab',
  standalone: true,
  imports: [CommonModule, CurrencyPipe, DecimalPipe, MatCardModule, MatButtonModule, MatIconModule, MatTableModule, BaseChartDirective],
  template: `
    @if (!report) { <p class="empty">Run a report to see the summary.</p> }
    @if (report) {
      <div class="summary">
        <div class="totals">
          <div class="stat"><span class="num">{{ report.summary.totalAmountCollected | currency }}</span><span class="lbl">Total collected</span></div>
          <div class="stat"><span class="num">{{ report.summary.totalPayments }}</span><span class="lbl">Payments</span></div>
          <div class="stat"><span class="num">{{ report.summary.averagePaymentAmount | currency }}</span><span class="lbl">Average</span></div>
          <div class="stat"><span class="num">{{ report.summary.netCollection | currency }}</span><span class="lbl">Net</span></div>
        </div>

        <mat-card class="chart-card" #chartCard>
          <h3>Payment method split</h3>
          <canvas baseChart [type]="'doughnut'" [data]="chartData" [options]="chartOptions"></canvas>
        </mat-card>

        <table mat-table [dataSource]="report.paymentsByMethod" class="method-table">
          <ng-container matColumnDef="methodType"><th mat-header-cell *matHeaderCellDef>Method</th>
            <td mat-cell *matCellDef="let m">{{ m.methodType }}</td></ng-container>
          <ng-container matColumnDef="paymentCount"><th mat-header-cell *matHeaderCellDef>Count</th>
            <td mat-cell *matCellDef="let m">{{ m.paymentCount }}</td></ng-container>
          <ng-container matColumnDef="totalAmount"><th mat-header-cell *matHeaderCellDef>Amount</th>
            <td mat-cell *matCellDef="let m">{{ m.totalAmount | currency }}</td></ng-container>
          <ng-container matColumnDef="percentage"><th mat-header-cell *matHeaderCellDef>%</th>
            <td mat-cell *matCellDef="let m">{{ m.percentage | number:'1.0-2' }}%</td></ng-container>
          <tr mat-header-row *matHeaderRowDef="methodCols"></tr>
          <tr mat-row *matRowDef="let row; columns: methodCols;"></tr>
        </table>
      </div>

      <div class="actions">
        <button mat-stroked-button (click)="exportPdf()"><mat-icon>picture_as_pdf</mat-icon> Export PDF</button>
        <button mat-stroked-button (click)="exportCsv()"><mat-icon>table_view</mat-icon> Export CSV</button>
      </div>
    }
  `,
  styles: [`
    .empty { color: #64748b; }
    .totals { display: flex; gap: 16px; flex-wrap: wrap; margin-bottom: 16px; }
    .stat { display: flex; flex-direction: column; padding: 12px 18px; border: 1px solid #e2e8f0; border-radius: 10px; min-width: 120px; }
    .stat .num { font-size: 20px; font-weight: 700; color: #1e293b; }
    .stat .lbl { font-size: 12px; color: #64748b; text-transform: uppercase; }
    .chart-card { max-width: 360px; padding: 16px; margin-bottom: 16px; }
    .chart-card h3 { margin: 0 0 8px; font-size: 14px; }
    .method-table { width: 100%; }
    .actions { display: flex; gap: 12px; margin-top: 12px; }
  `],
})
export class SummaryTabComponent {
  @ViewChild('chartCard', { read: ElementRef }) chartCard?: ElementRef<HTMLElement>;

  readonly methodCols = ['methodType', 'paymentCount', 'totalAmount', 'percentage'];

  private _report: PaymentReport | null = null;
  @Input() set report(value: PaymentReport | null) {
    this._report = value;
    this.chartData = this.buildChart(value);
  }
  get report(): PaymentReport | null { return this._report; }

  chartData: ChartData<'doughnut'> = { labels: [], datasets: [{ data: [] }] };
  readonly chartOptions: ChartConfiguration<'doughnut'>['options'] = {
    responsive: true,
    plugins: { legend: { position: 'right' } },
  };

  private buildChart(r: PaymentReport | null): ChartData<'doughnut'> {
    const rows = r?.paymentsByMethod ?? [];
    return {
      labels: rows.map(x => x.methodType),
      datasets: [{
        data: rows.map(x => x.totalAmount),
        backgroundColor: ['#1d4ed8', '#0ea5e9', '#22c55e', '#f59e0b', '#ef4444', '#a855f7', '#64748b'],
      }],
    };
  }

  exportCsv(): void {
    if (!this._report) return;
    const rows = this._report.paymentsByMethod.map(m =>
      [m.methodType, String(m.paymentCount), String(m.totalAmount), String(m.percentage)]);
    downloadCsv('payment-summary.csv', toCsv(['Method', 'Count', 'Amount', 'Percentage'], rows));
  }

  async exportPdf(): Promise<void> {
    if (!this._report) return;
    const tables = [{
      heading: 'Payment method split',
      head: ['Method', 'Count', 'Amount', '%'],
      body: this._report.paymentsByMethod.map(m =>
        [m.methodType, String(m.paymentCount), String(m.totalAmount), String(m.percentage)]),
    }];
    await exportReportPdf('payment-summary.pdf', this._report.reportTitle || 'Payment Summary',
      this.chartCard?.nativeElement ?? null, tables);
  }
}
